import { useLayoutEffect, useState } from "react";
import { AnimatePresence, motion, useDragControls, type PanInfo } from "framer-motion";
import { ChevronDown, ChevronUp, PanelRightClose } from "lucide-react";
import { useIsMobile, useLayoutMode } from "../../lib/useViewportSize";
import { publishInspectorInset } from "../../lib/inspectorInset";
import { cn } from "../../lib/cn";

interface Props {
  /** Whether anything is selected; controls mount of the inspector. */
  open: boolean;
  /** Deselect / close the inspector entirely. */
  onClose: () => void;
  /** Stable id of the selected item. Changing it resets to the remembered detent. */
  itemId?: string | null;
  /** Small visual at left of the header (color swatch, letter chip, etc.). */
  swatch?: React.ReactNode;
  /** Primary label, shown on the bar and in the expanded header. */
  title: string;
  /** Optional small secondary label under the title. */
  meta?: string;
  /** Compact actions on the collapsed bar (rotate, delete, …) so common
   *  edits don't need the full editor. */
  quickActions?: React.ReactNode;
  /** Card content shown when expanded. Scrolls inside the card. */
  children: React.ReactNode;
  /** Pinned action row under the scrolling body (rotate, delete, …). */
  footer?: React.ReactNode;
  /** Where the expanded editor docks. Defaults to a right-side sheet on
   *  landscape phones and a bottom sheet everywhere else. */
  placement?: "bottom" | "side";
  /**
   * Position classes for the expanded card's frame. The card is anchored
   * to the bottom (or top, for the side sheet) of this frame and never
   * grows past it.
   */
  expandedClassName?: string;
  /** Position classes for the collapsed bar. */
  minimizedClassName?: string;
  /** Marker after the title on the bar; `label` joins the bar's accessible name. */
  badge?: { icon: React.ReactNode; label: string };
  /** Replaces the collapsed bar while a mode owns it (Adjust mode). */
  modeBar?: React.ReactNode;
  /** Bump to open the editor from outside (e.g. a tapped canvas knob). */
  openRequest?: number;
}

/** bar: one-row summary · peek: header + first section · full: whole editor. */
type Detent = "bar" | "peek" | "full";
type OpenDetent = Exclude<Detent, "bar">;

const DETENT_KEY = "room-canvas:inspector-detent";

function rememberedOpenDetent(): OpenDetent {
  try {
    return sessionStorage.getItem(DETENT_KEY) === "full" ? "full" : "peek";
  } catch {
    return "peek";
  }
}

function rememberDetent(d: OpenDetent) {
  try {
    sessionStorage.setItem(DETENT_KEY, d);
  } catch {
    /* private mode — the detent just isn't remembered */
  }
}

const BOTTOM_FRAME =
  "left-3 right-3 top-[calc(var(--topbar-h)+0.5rem)] bottom-[var(--float-bottom)]";
/** Half the viewport at most (the furniture footer row needs ~20rem), so the
 *  canvas beside it still shows the selection. */
const SIDE_FRAME =
  "right-[calc(0.75rem+env(safe-area-inset-right))] top-[calc(var(--topbar-h)+0.5rem)] bottom-[var(--float-bottom)] w-[min(22.5rem,50vw)]";
const BAR_BOTTOM = "left-3 right-3 bottom-[var(--float-bottom)]";
const BAR_SIDE = "left-1/2 -translate-x-1/2 bottom-[var(--float-bottom)] w-[min(27rem,calc(100%-1.5rem))]";
/** Swipe distance / speed that moves the sheet one detent. */
const SWIPE_PX = 40;
const SWIPE_VELOCITY = 500;

/**
 * Selection inspector for phones. Selecting something always shows a
 * full-width bar (name, size, quick actions, "Edit") in the same spot, so
 * the canvas stays visible; "Edit" opens a sheet with two detents — peek
 * and full — that the header drags between. Swiping down steps full → peek
 * → bar; "Done" deselects. The last open detent is remembered for the
 * session (it is where "Edit" lands next time), and the sheet publishes
 * how much it covers (`inspectorInset.ts`) so the canvas can keep the
 * selection in view. Landscape phones dock the editor as a right-side
 * sheet. On desktop, selection properties live in the right context panel
 * — this component is mobile-only in practice.
 */
export function MinimizableInspector({
  open,
  onClose,
  itemId,
  swatch,
  title,
  meta,
  quickActions,
  children,
  footer,
  placement,
  expandedClassName,
  minimizedClassName,
  badge,
  modeBar,
  openRequest = 0,
}: Props) {
  const isMobile = useIsMobile();
  const layout = useLayoutMode();
  const side = (placement ?? (layout === "phoneLandscape" ? "side" : "bottom")) === "side";
  const startDetent = (): Detent => (isMobile ? "bar" : "full");
  const [detent, setDetentState] = useState<Detent>(startDetent);
  // Back to the bar whenever the selected item changes or the viewport
  // crosses the breakpoint. React's "reset state when prop changes"
  // pattern keeps this synchronous without an effect.
  const [lastKey, setLastKey] = useState<string | null | undefined>(itemId);
  const [lastIsMobile, setLastIsMobile] = useState(isMobile);
  if (lastKey !== itemId || lastIsMobile !== isMobile) {
    setLastKey(itemId);
    setLastIsMobile(isMobile);
    setDetentState(startDetent());
  }
  const [lastOpenRequest, setLastOpenRequest] = useState(openRequest);
  if (lastOpenRequest !== openRequest) {
    setLastOpenRequest(openRequest);
    if (isMobile && openRequest > 0) setDetentState(side ? "full" : rememberedOpenDetent());
  }
  const [cardEl, setCardEl] = useState<HTMLDivElement | null>(null);
  const dragControls = useDragControls();

  function setDetent(d: Detent) {
    setDetentState(d);
    if (isMobile && d !== "bar" && !side) rememberDetent(d);
  }

  const showBar = isMobile && detent === "bar";
  const sheet = isMobile && !side;

  useLayoutEffect(() => {
    if (!open || showBar || !isMobile || !cardEl) {
      publishInspectorInset({ bottom: 0, right: 0 });
      return;
    }
    const measure = () => {
      const r = cardEl.getBoundingClientRect();
      publishInspectorInset(
        side
          ? { bottom: 0, right: window.innerWidth - r.left }
          : { bottom: window.innerHeight - r.top, right: 0 }
      );
    };
    measure();
    const ro = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    ro?.observe(cardEl);
    window.addEventListener("resize", measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", measure);
      publishInspectorInset({ bottom: 0, right: 0 });
    };
  }, [open, showBar, isMobile, cardEl, side]);

  function onHeaderDragEnd(_e: PointerEvent | MouseEvent | TouchEvent, info: PanInfo) {
    if (info.offset.y < -SWIPE_PX || info.velocity.y < -SWIPE_VELOCITY) setDetent("full");
    else if (info.offset.y > SWIPE_PX || info.velocity.y > SWIPE_VELOCITY) {
      setDetent(detent === "full" ? "peek" : "bar");
    }
  }

  const expand = () => setDetent(side ? "full" : rememberedOpenDetent());

  const header = (
    <div
      className={cn(
        "flex shrink-0 items-center gap-2.5 border-b border-ink-100 pl-4 pr-2 pb-2",
        (!isMobile || side) && "pt-2"
      )}
    >
      {swatch}
      <div className="min-w-0 flex-1">
        <div className="truncate text-body font-semibold text-ink-900">{title}</div>
        {meta && <div className="truncate text-xs text-ink-500">{meta}</div>}
      </div>
      {sheet && (
        <button
          type="button"
          onClick={() => setDetent(detent === "full" ? "peek" : "full")}
          aria-label={detent === "full" ? "Show less" : "Show more"}
          className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-ink-500 hover:bg-ink-100 active:bg-ink-200 transition-colors"
        >
          {detent === "full" ? <ChevronDown className="size-5" /> : <ChevronUp className="size-5" />}
        </button>
      )}
      {isMobile && side && (
        <button
          type="button"
          onClick={() => setDetent("bar")}
          aria-label="Minimize"
          className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-ink-500 hover:bg-ink-100 active:bg-ink-200 transition-colors"
        >
          <PanelRightClose className="size-5" />
        </button>
      )}
      <button
        type="button"
        onClick={onClose}
        className="h-11 shrink-0 rounded-xl px-3.5 text-sm font-semibold text-accent-700 hover:bg-accent-50 active:bg-accent-100 transition-colors"
      >
        Done
      </button>
    </div>
  );

  return (
    <AnimatePresence mode="wait" initial={false}>
      {open && (showBar ? (
        <motion.div
          key={modeBar ? "mode-bar" : "bar"}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 8 }}
          transition={{ duration: 0.15 }}
          className={cn("absolute z-20", minimizedClassName ?? (side ? BAR_SIDE : BAR_BOTTOM))}
        >
          {modeBar ?? <div
            className="flex h-14 items-center gap-0.5 rounded-2xl bg-white p-1.5 ring-1 ring-ink-200 shadow-lg shadow-ink-900/15"
            data-testid="inspector-bar"
          >
            <button
              type="button"
              onClick={expand}
              className="flex h-11 min-w-0 flex-1 items-center gap-2.5 rounded-xl pl-2 pr-1 text-left active:bg-ink-50 transition-colors"
              aria-label={`Expand ${title} settings${badge ? `, ${badge.label}` : ""}`}
              aria-expanded={false}
              data-testid="inspector-pill"
            >
              {swatch && <div className="shrink-0">{swatch}</div>}
              <div className="min-w-0">
                <div className="flex min-w-0 items-center gap-1.5">
                  <div className="truncate text-body-sm font-semibold leading-tight text-ink-900">
                    {title}
                  </div>
                  {badge && (
                    <span className="shrink-0" title={badge.label} data-testid="inspector-badge">
                      {badge.icon}
                    </span>
                  )}
                </div>
                {meta && (
                  <div className="truncate text-xs leading-tight text-ink-500">{meta}</div>
                )}
              </div>
            </button>
            {quickActions}
            <button
              type="button"
              onClick={expand}
              className="ml-0.5 inline-flex h-11 shrink-0 items-center gap-1 rounded-xl bg-ink-900 pl-3.5 pr-2.5 text-body-sm font-semibold text-white active:bg-ink-800 transition-colors"
            >
              Edit
              <ChevronUp className="size-4 opacity-80" aria-hidden />
            </button>
          </div>}
        </motion.div>
      ) : (
        <motion.div
          key="card"
          initial={side ? { opacity: 0, x: 16 } : { opacity: 0, y: 12 }}
          animate={side ? { opacity: 1, x: 0 } : { opacity: 1, y: 0 }}
          exit={side ? { opacity: 0, x: 16 } : { opacity: 0, y: 12 }}
          transition={{ duration: 0.18 }}
          className={cn(
            "absolute z-[25] flex flex-col pointer-events-none",
            side ? "justify-start" : "justify-end",
            expandedClassName ?? (side ? SIDE_FRAME : BOTTOM_FRAME)
          )}
        >
          <motion.div
            ref={setCardEl}
            drag={sheet ? "y" : false}
            dragListener={false}
            dragControls={dragControls}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0.08, bottom: 0.4 }}
            dragSnapToOrigin
            onDragEnd={onHeaderDragEnd}
            className={cn(
              "pointer-events-auto flex min-h-0 flex-col overflow-hidden rounded-2xl bg-white ring-1 ring-ink-200 shadow-xl shadow-ink-900/10",
              sheet
                ? cn(
                    "transition-[max-height] duration-200 ease-out",
                    detent === "full" ? "max-h-[100%]" : "max-h-[min(52%,26rem)]"
                  )
                : side
                  ? "max-h-[100%]"
                  : "max-h-[min(100%,72dvh)]"
            )}
            data-testid="inspector-card"
            data-detent={isMobile ? detent : undefined}
          >
            {sheet ? (
              // The whole header drags between detents; the grabber row alone
              // taps to minimize.
              <div className="shrink-0 touch-none" onPointerDown={(e) => dragControls.start(e)}>
                <button
                  type="button"
                  onClick={() => setDetent("bar")}
                  className="flex h-11 w-full items-center justify-center"
                  aria-label="Minimize"
                >
                  <span className="h-1.5 w-10 rounded-full bg-ink-300" />
                </button>
                {header}
              </div>
            ) : (
              header
            )}
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pt-2">
              {children}
            </div>
            {footer && (
              <div className="shrink-0 border-t border-ink-100 bg-white">{footer}</div>
            )}
          </motion.div>
        </motion.div>
      ))}
    </AnimatePresence>
  );
}
